// Inline-Edit fuer Sticky/Text/Shape/Requirement/Pain/Dependency.
// Auto-resize per `field-sizing: content` mit JS-Fallback ueber scrollHeight.
// Esc cancel, Blur commit, Ctrl/Cmd+Enter commit. Pointer-Events stoppen, damit
// kein Drag in ObjectLayer ausgeloest wird.

import { type KeyboardEvent, type PointerEvent, useEffect, useRef, useState } from "react";

export type EditableCommitIntent = "plain" | "sibling" | "child" | "unindent";

interface EditableTextProps {
  initialValue: string;
  onCommit: (next: string) => void;
  onCancel: () => void;
  ariaLabel?: string;
  /** TAB im Edit-Mode: aktuellen Stand committen + neuen Klon mit gleichen Eigenschaften
   *  rechts daneben erzeugen (Miro-Verhalten). Nur wenn vom Container bereitgestellt. */
  onTabClone?: () => void;
  /** Mindmap-Mode: ENTER/TAB/Shift+TAB committen mit einer Tree-Intent
   *  (sibling/child/unindent). Nur wenn vom Container bereitgestellt; wenn gesetzt
   *  uebersteuert es die `onTabClone`-Legacy. */
  onCommitWithIntent?: (intent: EditableCommitIntent) => void;
}

export function EditableText({
  initialValue,
  onCommit,
  onCancel,
  ariaLabel,
  onTabClone,
  onCommitWithIntent,
}: EditableTextProps): JSX.Element {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const commit = (): void => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(value);
  };

  const cancel = (): void => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCancel();
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      commit();
      return;
    }
    // Mindmap-Tree-Intents — wenn der Container sich dafuer registriert hat, schlucken
    // wir ENTER (Sibling) und TAB/Shift+TAB (Child/Unindent) und propagieren den Intent.
    if (onCommitWithIntent) {
      if (e.key === "Enter" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        commit();
        onCommitWithIntent("sibling");
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        commit();
        onCommitWithIntent(e.shiftKey ? "unindent" : "child");
        return;
      }
    } else if (e.key === "Tab" && onTabClone) {
      e.preventDefault();
      commit();
      onTabClone();
    }
  };

  const stopPointer = (e: PointerEvent<HTMLTextAreaElement>): void => {
    e.stopPropagation();
  };

  return (
    <textarea
      ref={ref}
      className="canvas-editable"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKey}
      onPointerDown={stopPointer}
      onPointerMove={stopPointer}
      onPointerUp={stopPointer}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
      rows={1}
    />
  );
}
